import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import classnames from 'classnames';
import { isEmpty, capitalize } from 'lodash';
import { autopayIcon, monitorIcon } from 'images';
import {
  selectCurrentPaymentMethod,
  selectStatementData,
  selectCreditCards,
  selectBankAccounts,
} from 'containers/PrimoProfile/selectors';
import { fetchPaymentMethods } from 'containers/PrimoProfile/actions';
import { Button, Div, F } from 'components/Ui';
import { showDrawer as showDrawerRequest } from 'containers/DrawerManager/actions';
import Loader from 'components/Loader';
import { titleCase } from 'utils/string';
import commonStyles from 'containers/PrimoProfile/styles.scss';
import styles from './styles.scss';

import { EDIT_BILLING_CONTACT, EDIT_BILLING_ADDRESS } from '../constants';

const BillingDetailedInfo = ({
  currentPaymentMethod,
  bankAccounts,
  creditCards,
  showPaymentMethods,
  showInvoiceSettings,
  billingContact,
  showDrawer,
  ...props
}) => {
  const isFetching = false;

  let contactPhoneNumber = '';
  let contactEmailAddress = '';
  let contactName = '';

  const hasBillingContacts = !isEmpty(billingContact.contacts);

  if (hasBillingContacts) {
    const contactPhone = billingContact.contacts[0].contactPoints.find(
      (item) => item.contactType === 'phone',
    );
    const contactEmail = billingContact.contacts[0].contactPoints.find(
      (item) => item.contactType === 'email',
    );
    contactPhoneNumber = contactPhone ? contactPhone.phoneNumber : '';
    contactEmailAddress = contactEmail ? contactEmail.email : '';

    const contact = billingContact.contacts[0];
    contactName = `${contact.firstName} ${contact.lastName}`;
  }

  useEffect(() => {
    props.fetchPaymentMethods();
  }, [currentPaymentMethod]);

  const showEditBillingContact = false;

  return (
    <Loader loading={isFetching} local>
      <div className={commonStyles.section}>
        <div className={classnames(commonStyles.subBlock, styles.subBlock)}>
          <Div className={commonStyles.subTitle}>Billing Contact</Div>
          <div>
            <Fragment>
              {hasBillingContacts ? (
                <div>
                  {contactName}
                  {showEditBillingContact && (
                    <div>
                      <Button
                        className={styles.invoiceButton}
                        text="Edit"
                        onClick={() => showDrawer(EDIT_BILLING_CONTACT)}
                        link
                        bold
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div />
              )}
            </Fragment>
          </div>
        </div>
        <div className={classnames(commonStyles.subBlock, styles.subBlock)}>
          <Div className={commonStyles.subTitle}>Billing Email</Div>
          <div>
            <Fragment>
              <div>{contactEmailAddress}</div>
            </Fragment>
          </div>
        </div>
      </div>
      <div className={commonStyles.section}>
        <div className={classnames(commonStyles.subBlock, styles.subBlock)}>
          <Div className={commonStyles.subTitle}>Billing Address</Div>
          <div>{titleCase(billingContact.address1)}</div>
          {billingContact.address2 && <div>{billingContact.address2}</div>}
          <div>
            {titleCase(billingContact.city)},{' '}
            {billingContact.stateOrProvinceCode} {billingContact.postalCode}
          </div>
          <div>
            <Button
              className={styles.invoiceButton}
              text="Edit"
              onClick={() => showDrawer(EDIT_BILLING_ADDRESS)}
              link
              bold
            />
          </div>
        </div>
        <div className={classnames(commonStyles.subBlock, styles.subBlock)}>
          <Fragment>
            <Div className={commonStyles.subTitle}>Billing Preferences</Div>
            <div className={commonStyles.icons}>
              {currentPaymentMethod && currentPaymentMethod.autopay && (
                <div
                  className={classnames(
                    commonStyles.iconContainer,
                    styles.iconContainerOverride,
                  )}
                >
                  <img
                    className={commonStyles.icon}
                    src={autopayIcon}
                    alt="autopay"
                    aria-hidden="true"
                  />
                  <F>Autopay enabled</F>
                </div>
              )}
              <div className={commonStyles.iconContainer}>
                <img
                  className={commonStyles.icon}
                  src={monitorIcon}
                  alt="paperless"
                  aria-hidden="true"
                />
                <F>eInvoice</F>
              </div>
              {hasBillingContacts && (
                <div>
                  <Button
                    className={styles.invoiceButton}
                    text="Manage Billing Preferences"
                    onClick={showInvoiceSettings}
                    link
                    bold
                  />
                </div>
              )}
            </div>
            {props.onEdit && (
              <Button text="Edit preferences" onClick={props.onEdit} link />
            )}
          </Fragment>
        </div>
      </div>
      <div className={commonStyles.section}>
        <div className={classnames(commonStyles.subBlock, styles.subBlock)}>
          <Div className={commonStyles.subTitle}>Billing Phone</Div>
          <div>
            <Fragment>
              <div>{contactPhoneNumber}</div>
            </Fragment>
          </div>
        </div>
        <div
          className={classnames(commonStyles.subBlock, styles.subBlockOverRide)}
        >
          <Div className={commonStyles.subTitle}>Payment Methods</Div>
          <div>
            <Fragment>
              <div>
                {creditCards.map((creditCard) => (
                  <div className={styles.creditCardText} key={creditCard.last4}>
                    <F>Credit/debit card ending in</F> {creditCard.last4}
                  </div>
                ))}
                {bankAccounts.map((bankAccount) => (
                  <div>
                    <F>{capitalize(bankAccount.accountType)}</F>{' '}
                    <F>ending in</F> {bankAccount.last4}
                  </div>
                ))}
              </div>
              {hasBillingContacts && (
                <Button
                  text="Manage Payment Methods"
                  onClick={showPaymentMethods}
                  link
                  bold
                />
              )}
            </Fragment>
          </div>
        </div>
      </div>
    </Loader>
  );
};

BillingDetailedInfo.propTypes = {
  currentPaymentMethod: PropTypes.object,
  statementSettings: PropTypes.object,
  fetchPaymentMethods: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  creditCards: PropTypes.array,
  bankAccounts: PropTypes.array,
  showPaymentMethods: PropTypes.func,
  showInvoiceSettings: PropTypes.func,
  billingContact: PropTypes.object,
  showDrawer: PropTypes.func,
};

BillingDetailedInfo.defaultProps = {
  currentPaymentMethod: null,
  statementSettings: {},
  onEdit: null,
};

const mapStateToProps = createStructuredSelector({
  currentPaymentMethod: selectCurrentPaymentMethod(),
  statementSettings: selectStatementData(),
  creditCards: selectCreditCards(),
  bankAccounts: selectBankAccounts(),
});

const mapDispatchToProps = {
  showDrawer: showDrawerRequest,
  fetchPaymentMethods,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(BillingDetailedInfo);
